import type { ToolCallsSummary } from '@ace-tracker/shared';
import { SESSIONS_METRIC_INFO } from '../metricInfo';
import { TOOL_COLORS } from '../toolColors';
import { TOOL_LABELS } from './Sidebar';
import { TableCard } from './TableCard';

const DASH = '—';

/** Failure rate over calls with a known outcome; a dash when none is known. */
function failureRate(c: { knownOutcomes: number; failed: number }): string {
  return c.knownOutcomes > 0 ? `${((c.failed / c.knownOutcomes) * 100).toFixed(1)}%` : DASH;
}

function share(part: number, whole: number): string {
  return whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : DASH;
}

/**
 * Tool, MCP server and skill usage for Claude Code and Codex (E2.5). Needs
 * collector 1.6.0+ with the `sanitization.sendToolNames` opt-in. Aggregate
 * only — no per-developer ranking.
 */
export function ToolCallsSection({
  toolCalls,
  SectionHeading,
}: {
  toolCalls: ToolCallsSummary;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  return (
    <section>
      <SectionHeading title="Tools, MCP servers and skills" hint="Claude Code and Codex tool calls" info={SESSIONS_METRIC_INFO.toolCalls} />
      {toolCalls.calls === 0 ? (
        <p className="rounded-card border border-border bg-surface p-5 text-sm text-text-muted shadow-sm">
          No tool-call records in this range. They need collector 1.6.0 or later with{' '}
          <code className="font-mono text-xs">sanitization.sendToolNames</code> turned on. It is off by default; see the collector's
          PRIVACY.md before turning it on.
        </p>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            {toolCalls.bySource.map((s) => (
              <div key={s.tool} className="relative rounded-card border border-border bg-surface p-5 shadow-sm">
                <span className="absolute inset-y-0 left-0 w-[3px] rounded-l-card" style={{ backgroundColor: TOOL_COLORS[s.tool] }} />
                <h3 className="text-sm font-semibold text-text-default">{TOOL_LABELS[s.tool]}</h3>
                <dl className="mt-3 grid grid-cols-3 gap-x-3 gap-y-3 text-sm">
                  <Metric label="Tool calls" value={s.calls.toLocaleString()} />
                  <Metric label="Failure rate" value={failureRate(s)} />
                  <Metric label="Repeats" value={share(s.repeats, s.calls)} />
                </dl>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
            <TableCard title="Most used tools" info={SESSIONS_METRIC_INFO.toolCalls} empty={toolCalls.byTool.length === 0} emptyText="No tool calls.">
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-3">Tool</th>
                  <th className="pb-2.5 pr-3">Source</th>
                  <th className="pb-2.5 pr-3 text-right">Calls</th>
                  <th className="pb-2.5 pr-3 text-right">Sessions</th>
                  <th className="pb-2.5 pr-3 text-right">Failure rate</th>
                  <th className="pb-2.5 text-right">Repeats</th>
                </tr>
              </thead>
              <tbody>
                {toolCalls.byTool.map((t) => (
                  <tr key={`${t.tool}|${t.mcpServer}|${t.toolName}`} className="border-b border-border last:border-0">
                    <td className="max-w-[260px] truncate py-2 pr-3 font-mono text-xs text-text-default" title={t.mcpServer ? `${t.mcpServer} · ${t.toolName}` : t.toolName}>
                      {t.mcpServer && <span className="text-text-muted">{t.mcpServer} · </span>}
                      {t.toolName}
                    </td>
                    <td className="py-2 pr-3 text-text-muted">{TOOL_LABELS[t.tool]}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{t.calls.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{t.sessions.toLocaleString()}</td>
                    <td className="py-2 pr-3 text-right tabular-nums">{failureRate(t)}</td>
                    <td className="py-2 text-right tabular-nums">{share(t.repeats, t.calls)}</td>
                  </tr>
                ))}
              </tbody>
            </TableCard>

            <div className="flex flex-col gap-5">
              <TableCard
                title="MCP servers"
                info={SESSIONS_METRIC_INFO.toolCalls}
                empty={toolCalls.byMcpServer.length === 0}
                emptyText="No MCP tool calls in this range."
              >
                <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                  <tr>
                    <th className="pb-2.5 pr-3">Server</th>
                    <th className="pb-2.5 pr-3 text-right">Tools used</th>
                    <th className="pb-2.5 pr-3 text-right">Calls</th>
                    <th className="pb-2.5 pr-3 text-right">Sessions</th>
                    <th className="pb-2.5 text-right">Failure rate</th>
                  </tr>
                </thead>
                <tbody>
                  {toolCalls.byMcpServer.map((s) => (
                    <tr key={s.mcpServer} className="border-b border-border last:border-0">
                      <td className="py-2 pr-3 font-mono text-xs text-text-default">{s.mcpServer}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{s.tools.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{s.calls.toLocaleString()}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{s.sessions.toLocaleString()}</td>
                      <td className="py-2 text-right tabular-nums">{failureRate(s)}</td>
                    </tr>
                  ))}
                </tbody>
              </TableCard>

              <TableCard title="Skills" info={SESSIONS_METRIC_INFO.toolCalls} empty={toolCalls.bySkill.length === 0} emptyText="No skill calls in this range.">
                <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                  <tr>
                    <th className="pb-2.5 pr-3">Skill</th>
                    <th className="pb-2.5 pr-3 text-right">Calls</th>
                    <th className="pb-2.5 text-right">Sessions</th>
                  </tr>
                </thead>
                <tbody>
                  {toolCalls.bySkill.map((s) => (
                    <tr key={s.skillName} className="border-b border-border last:border-0">
                      <td className="py-2 pr-3 font-mono text-xs text-text-default">{s.skillName}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{s.calls.toLocaleString()}</td>
                      <td className="py-2 text-right tabular-nums">{s.sessions.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </TableCard>
            </div>
          </div>
        </>
      )}
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-text-faint">{label}</dt>
      <dd className="text-lg font-semibold tabular-nums text-text-default">{value}</dd>
    </div>
  );
}
